<template>
  <div class="qb-breakdown-filters-panel">
    <app-time-periods v-if="query && query.period && periodsData"
      :queryPeriod="query.period"
      :periods="periodsData"
      :period_types="period_types"
      @setQueryTimePeriod="setQueryTimePeriod"
    />

    <app-articles-status
      :queryArticleStatus="query.article_status"
      @setArticleStatus="$emit('setQueryEntity', $event)"
    />

    <app-standard-breakdown
      :options="standardBreakdownOptions"
      :activeOption="activeOption"
      :activeParentOption="activeParentOption"
      :selectedOptions="selectedOptions"
      :isListLoading="standardBreakdown.isBusy"
      :filters="filters"
      :onDrag="onDrag"
      :onDragEnd="onDragEnd"
      @getSubOptions="getSubOptions"
      @saveBreakdownTextEntry="saveBreakdownTextEntry"
      @deleteBreakdownTextEntry="deleteBreakdownTextEntry"
      @saveTimeBreakdownSettings="saveTimeBreakdownSettings"
      @setActiveOption="setActiveOption"
      @setSelectedOptions="setSelectedOptions"
      @toggleExpandedOption="toggleExpandedOption"
    />

    <app-outputs
      :outputList="outputs"
      :defaultOutput="query.default_output"
      :queryOutputs="query.outputs"
      :queryOutputSettings="query.output_settings || []"
      :queryRows="query.rows"
      @setQueryOutputs="$emit('setQueryEntity', $event)"
    />

    <app-external-filters
      :dragging="dragging && dragging.indexOf(ALLOW_DRAG.EXTERNAL_FILTERS) !== -1"
      :filters="query.external_filters"
    />
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import _ from 'lodash';

  import TimePeriods from './time_periods';
  import StandardBreakdown from './standard_breakdown/';
  import Outputs from './outputs';
  import ExternalFilters from './external_filters';
  import ArticlesStatus from './articles_status';

  import { QUERIES } from '../../config/const';

  export default {
    props: {
      query: {
        type: Object,
        required: true
      },
      onDrag: {
        type: Function,
        default: () => null
      },
      onDragEnd: {
        type: Function,
        default: () => null
      },
      dragging: {
        type: [Array, Boolean],
        default: false
      },
      outputs: {
        type: Array,
        default: () => []
      },
      filters: {
        type: Array,
        default: () => []
      },
      localTextOptions: {
        type: Array,
        default: () => []
      },
      localHeadlineOptions: {
        type: Array,
        default: () => []
      }
    },
    components: {
      'app-time-periods': TimePeriods,
      'app-standard-breakdown': StandardBreakdown,
      'app-outputs': Outputs,
      'app-external-filters': ExternalFilters,
      'app-articles-status': ArticlesStatus
    },
    computed: {
      ...mapState({
        periods: state => state.queryBuilder.periods.periods,
        standardBreakdowns: state => state.queryBuilder.standardBreakdowns.standardBreakdowns,
        standardBreakdown: state => state.queryBuilder.standardBreakdown.standardBreakdown
      }),
      periodsData() { return this.periods && this.periods.data && this.periods.data.periods },
      period_types() { return this.periods.data && this.periods.data.types },

      projectId() {
        return this.$route.params.project_id;
      },
      queryId() {
        return this.$route.params.query_id;
      }
    },
    created () {
      if(!this.standardBreakdowns.isBusy) {
        this.getStandardBreakdowns({
          projectId: this.projectId,
          queryId: this.queryId
        });
      }

      if(!this.periods.isBusy) {
        this.getPeriods({
          projectId: this.projectId,
          queryId: this.queryId
        });
      }
    },
    watch: {
      'standardBreakdowns.data': {
        handler(value) {
          this.standardBreakdownOptions = _.cloneDeep(value).map(sb => {

            return sb;
          });
        },
        deep: true
      },

      'standardBreakdown.data': {
        handler(value) {
          if(!value) {
            return;
          }

          this.standardBreakdownOptions = this.setStandardBreakdownSubOptions(value, this.standardBreakdownOptions);
        },
        deep: true
      }
    },
    methods: {
      ...mapActions({
        'getPeriods': 'queryBuilder/periods/getPeriods',
        'getStandardBreakdowns': 'queryBuilder/standardBreakdowns/getStandardBreakdowns',
        'queryUpdateFilterTag': 'queryBuilder/query/updateFilterTag',
        'getStandardBreakdown': 'queryBuilder/standardBreakdown/getStandardBreakdown',
        'addStandardBreakdownEntry': 'queryBuilder/standardBreakdown/addStandardBreakdownEntry',
        'deleteStandardBreakdownSubOptions': 'queryBuilder/standardBreakdown/deleteStandardBreakdownSubOptions',
        'editStandardBreakdownEntry': 'queryBuilder/standardBreakdown/editStandardBreakdownEntry',
        'saveStandardBreakdownSettings': 'queryBuilder/standardBreakdown/saveStandardBreakdownSettings',
        'updateQuerySettings': 'queryBuilder/query/updateQuerySettings',
      }),

      setStandardBreakdownSubOptions (standardBreakdownValue, subOptions) {
        return subOptions.map(option => {
          if (standardBreakdownValue.id === option.id && standardBreakdownValue.parent_id === option.parent_id) {
            option.sub_options = _.cloneDeep(standardBreakdownValue.sub_options);
            option.sub_options?.find(o => {
              if (
                o?.settings?.custom_compare_date_range?.value?.custom_compare_date_range &&
                this.query?.period?.custom_compare_date_range
              ) {
                o.settings.custom_compare_date_range.value.custom_compare_date_range = this.query?.period?.custom_compare_date_range;
                return true;
              }
              return false;
            });
            option.has_children = standardBreakdownValue.has_children;
            option.settings = standardBreakdownValue.settings;
          }
          else if (option.sub_options && option.sub_options.length > 0) {
            this.setStandardBreakdownSubOptions(standardBreakdownValue, option.sub_options);
          }

          return option;
        })
      },

      getBreakdownEntryId (value) {
        return this.standardBreakdownOptions.find(option => option.name.toLowerCase() === value).id;
      },

      setQueryTimePeriod ({entity, value}) {
        let period = _.cloneDeep(this.query.period);
        period[entity] = value;

        this.$emit('setQueryEntity', { entity: 'period', value: period });
      },

      setActiveOption (option) {
        if(option && option.expandable) {
          this.activeParentOption = option;

          if (option.parent_id) {
            const parent = this.standardBreakdownOptions.find(opt => opt.id === option.parent_id);
            if (parent) {
              this.activeParentOption = parent;
            }
          }
          
        }

        this.activeOption = option;
      },

      toggleExpandedOption (option) {
        this.setSelectedOptions();

        if (option.is_active) {
          this.standardBreakdownOptions = this.deactivateBreakdownOptions(this.standardBreakdownOptions, option.id);
        }
        else {
          //deactivate other options with same parent
          this.standardBreakdownOptions = this.deactivateBreakdownOptionsByParent(option.parent_id || -1, this.standardBreakdownOptions);

          this.standardBreakdownOptions = this.activateBreakdownOption(option.id, this.standardBreakdownOptions);
          //get must be done always to clear searches
          this.getSubOptions({optionId: option.id});
        }

        this.setActiveOption(option);
      },

      activateBreakdownOption (optionId, subOptions) {
        return subOptions.map(option => {

          if(option.id === optionId) {
            option.is_active = true;
          }
          else if(option.sub_options && option.sub_options.length > 0) {
            this.activateBreakdownOption(optionId, option.sub_options);
          }

          return option;
        });
      },

      deactivateBreakdownOptions (subOptions, optionId) {
        return subOptions.map(option => {
          if(!optionId || (optionId && option.id === optionId)) {
            option.is_active = false;

            // deactivate all children
            if (option.sub_options) {
              this.deactivateBreakdownOptions(option.sub_options);
            }
          }
          else if(option.sub_options && option.sub_options.length > 0) {
            this.deactivateBreakdownOptions(option.sub_options, optionId);
          }

          return option;
        });
      },

      deactivateBreakdownOptionsByParent (parent, subOptions) {
        return subOptions.map(option => {
          const optionParent = option.parent_id || -1;
          if(parent === optionParent) {
            option.is_active = false;

            // deactivate all children
            if (option.sub_options && option.sub_options.length > 0) {
              this.deactivateBreakdownOptions(option.sub_options);
            }
          }

          else if(option.sub_options && option.sub_options.length > 0) {
            this.deactivateBreakdownOptionsByParent(parent, option.sub_options);
          }

          return option;
        });
      },

      setSelectedOptions (options = []) {
        if(!Array.isArray(options)) {
          this.selectedOptions = []
          this.selectedOptions.push(options)
        }
        else this.selectedOptions = options;
      },

      getSubOptions ({optionId, query}) {
        let entryObject = this.standardBreakdownOptions.find(option => option.id === optionId);

        if(!this.queryId && entryObject && (entryObject.value === 'text' || entryObject.value === 'headline')) {
          const localOptions = entryObject.value === 'headline' ? this.localHeadlineOptions : this.localTextOptions;
          let options = _.cloneDeep(localOptions);
          if(query && query.trim() !== '' && entryObject) {
            options = options.filter(text => text.name.toLowerCase().includes(query.toLowerCase()));
          }
          //Limit to first 40
          options = options && options.length > 0 ? options.splice(0, 39) : [];

          this.standardBreakdownOptions = this.standardBreakdownOptions.map(option => {
            if(option.id === entryObject.id && option.parent_id === entryObject.parent_id) {
              option.has_children = options.length > 0;
              option.sub_options = options;
            }
            return option;
          });
        }
        else {
          let pagination = {perPage: 40};

          if (query && query.trim() !== '') {
            pagination.query = query;
          }

          if (!this.standardBreakdown.isBusy) {
            this.getStandardBreakdown({
              projectId: this.projectId,
              queryId: this.queryId,
              breakdownId: optionId,
              pagination
            });
          }
        }
      },

      saveBreakdownTextEntry (textModel, type) {
        const breakdownId = textModel.id ? this.activeOption.parent_id : this.activeOption.id;
        if (this.queryId) {
          if (textModel.id) {
            this.editStandardBreakdownEntry({
              projectId: this.projectId,
              queryId: this.queryId,
              breakdownId: this.activeOption.parent_id,
              entry: textModel
            });
          } else {
            this.addStandardBreakdownEntry({
              projectId: this.projectId,
              queryId: this.queryId,
              breakdownId: this.activeOption.id,
              entry: textModel
            });
          }
        } else {
          this.standardBreakdownOptions = this.addUpdateTextEntry(breakdownId, textModel, this.standardBreakdownOptions, type);
        }

        this.updateQueryCustomBreakdown({breakdown_id: breakdownId, option_id: textModel.id, settings: textModel});
      
        this.queryUpdateFilterTag({
          entry: textModel,
          projectId: this.projectId,
          breakdownId: this.activeOption.parent_id,
          sendToSave: this.queryId
        });
      },

      updateQueryCustomBreakdown (option) {
        let updated = false;
        let customBreakdown = [];
        let dataModel = _.cloneDeep(this.query.custom_breakdown);

        customBreakdown = dataModel.map (breakdown => {
          if(JSON.stringify(Object.keys(breakdown)) === JSON.stringify(Object.keys(option.settings))) {
            breakdown = option.settings;
            updated = true;
          }
          return breakdown;
        });

        if(!updated) customBreakdown.push(option);

        this.$emit('setQueryEntity', { entity: 'custom_breakdown', value: customBreakdown });
      },

      addUpdateTextEntry (breakdownId, textModel, subOptions, type) {
        let options = subOptions.map(opt => {
          if (opt.id === breakdownId && !opt.parent_id) {
            opt.has_children = true;
            if(textModel.id) {
              const index = opt.sub_options.findIndex(textEntry => textEntry.id === textModel.id);
              if (index != null) {
                this.$set(opt.sub_options, index, textModel);
              }
            }
            else {
              if(!opt.sub_options) {
                opt.sub_options = [];
              }

              textModel.id = opt.sub_options.length > 0 ? opt.sub_options[opt.sub_options.length -1].id + 1 : 1;
              textModel.parent_id = breakdownId;
              textModel.draggable_to = [QUERIES.ALLOW_DRAG.AXIS, QUERIES.ALLOW_DRAG.FILTERS];
              opt.sub_options = [...opt.sub_options, textModel];
            }
          }
          return opt;
        });

        const newOptions = options.find(option => option.value === type).sub_options;
        //save text entries to local storage
        this.$emit('setLocalTextOptions', newOptions, type);
        return options;
      },

      updateQueryPeriod(model) {
        if(!model?.custom_compare_date_range) return;
        let queryModel = _.cloneDeep(this.query);
        if(queryModel?.period) {
          queryModel.period['custom_compare_date_range'] = model?.custom_compare_date_range?.custom_compare_date_range;
          // update the store's value
          this.$store.commit('queryBuilder/query/UPDATE_QUERY', queryModel);
          // update the local query obj
          this.$emit('setQueryEntity', { entity: 'period', value: queryModel?.period, noRequest: true });
        }
      },

      saveTimeBreakdownSettings (option, model) {
        this.selectedOptions = [];
        let breakdownId, entryId;

        this.updateQueryPeriod(model);

        if(this.queryId) {
          const hasParentBreakdown = option.parent_id;
          if (hasParentBreakdown) {
            breakdownId = option.parent_id;
            entryId = option.id;
          } else {
            breakdownId = option.id;
          }
          this.saveStandardBreakdownSettings({
            projectId: this.projectId,
            queryId: this.queryId,
            breakdownId: breakdownId,
            entryId: entryId,
            settingsModel: model
          });
        }
        else {
          this.standardBreakdownOptions = this.updateSettings(option, model, this.standardBreakdownOptions);
        }

        this.updateQuerySettings({
          projectId: this.projectId,
          queryId: this.queryId,
          breakdownId: breakdownId,
          entryId: entryId,
          settings: model
        });

        this.updateQueryCustomBreakdown({breakdown_id: breakdownId, option_id: entryId, settings: model});
      },

      updateSettings (option, settings, subOptions) {
        return subOptions.map(opt => {
          if (opt.id === option.id && opt.parent_id === option.parent_id) {
            Object.keys(settings).map(setting => {
              opt.settings[setting].value = settings[setting];
            });
          }
          else if (opt.sub_options) {
            opt.sub_options = this.updateSettings(option, settings, opt.sub_options);
          }
          return opt;
        });
      },
      deleteBreakdownTextEntry (textModel) { 

        this.removeSubOption(textModel);

        this.deleteStandardBreakdownSubOptions({
          projectId: this.projectId,
          queryId: this.queryId,
          breakdownId: this.activeOption.parent_id,
          entry: textModel
        });
      },
      removeSubOption(record) {
        const standardBreakdown = this.standardBreakdownOptions
          .find(item => item.id === record.parent_id);

        if (!standardBreakdown) return;

        standardBreakdown.sub_options = standardBreakdown.sub_options
          .filter(item => item.id !== record.id)
        
        const customBreakdowns = this.query.custom_breakdown.filter(breakdown => !(breakdown.breakdown_id === record.parent_id && breakdown.option_id === record.id));
        this.$emit('setQueryEntity', { entity: 'custom_breakdown', value: customBreakdowns });
      }
    },
    data() {
      return {
        standardBreakdownOptions: [],
        ALLOW_DRAG: QUERIES.ALLOW_DRAG,
        activeOption: undefined,
        activeParentOption: undefined,
        selectedOptions: []
      }
    }
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/breakdown-filters-panel";
</style>